export interface BlogPost {
  slug: string;
  publishedAt: string;
  readingMinutes: number;
  en: PostContent;
  es: PostContent;
}

export interface PostContent {
  title: string;
  description: string;
  keywords: string;
  body: string; // HTML
}

export const posts: BlogPost[] = [
  {
    slug: 'whiteout-survival-bot-guide',
    publishedAt: '2026-07-21',
    readingMinutes: 9,
    en: {
      title: 'Whiteout Survival Bot: The Complete 2026 Guide (Setup, Tricks & Strategy)',
      description: 'Everything about using a bot in Whiteout Survival: what it automates, how to set it up safely on your own PC, controlling it from your phone, remote access with Chrome Remote Desktop, and the strategies that make a bot worth it.',
      keywords: 'whiteout survival bot, bot for whiteout survival, whiteout survival auto farm, whiteout survival automation, whiteout survival macro, whiteout survival auto gather',
      body: `
<div class="blog-hero-banner blog-hero-banner-bot"><div class="blog-hero-banner-content"><span class="blog-hero-banner-tag">🤖 Bot Guide</span><p class="blog-hero-banner-title">Your city, playing 24/7.<br/>Everything a Whiteout Survival bot can do.</p></div></div>

<p>Whiteout Survival quietly punishes downtime. Empty march queues, idle training camps and unhunted beasts are capacity you never get back — and the players at the top of your server simply have less downtime than you. A <strong>Whiteout Survival bot</strong> closes that gap: it plays the repetitive part of the game (gathering, training, hunting) around the clock, so your city keeps growing while you sleep, work or study. This guide covers how bots work, how to run one safely, and the strategies that actually make one worth it.</p>

<div class="blog-video"><iframe src="https://www.youtube-nocookie.com/embed/dhouCxslNb4?rel=0" title="White Bot — Whiteout Survival bot running live" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>
<p class="blog-video-cap">White Bot running a real Whiteout Survival account — hunting, gathering and the daily loop, controlled from the browser.</p>

<h2>What a Whiteout Survival Bot Actually Does</h2>
<p>A bot is software that plays the game the way you would: it looks at the screen, recognizes what it sees, and taps. A good one automates the loop that eats your day:</p>
<ul>
  <li><strong>Resource gathering</strong> — keeps every march queue on resource tiles, resending marches as they return, all day and all night.</li>
  <li><strong>Troop training</strong> — restarts training queues the moment they finish.</li>
  <li><strong>Beast hunting</strong> — spends stamina on daily hunts (and event cryptids when they are active).</li>
  <li><strong>Alliance help, healing, shields</strong> — the small taps that keep a city healthy without you babysitting it.</li>
</ul>
<p>What a bot does <em>not</em> do: play events for you, make strategic decisions, or replace you in wars. Think of it as outsourcing the chores so your play time goes to the parts that need a human.</p>

<h2>Your Options in 2026</h2>

<h3>BlueStacks macros and generic auto-clickers</h3>
<p>Free, but they replay fixed coordinates blindly. One popup, one level-up screen or one attack and the macro taps the wrong thing for hours. No remote control, no safety logic, and they break with every game update.</p>

<h3>Cloud bots</h3>
<p>Someone else's server plays your account — which means handing your account login to a stranger. That is the single biggest risk in this space: your account IS your progress. Convenient, but you are trusting a third party with the keys.</p>

<h3>A desktop bot on your own PC (what White Bot is)</h3>
<p><strong><a href="/">White Bot</a></strong> runs on your own Windows PC, inside your own BlueStacks. You link your game account yourself, in your emulator — <strong>your credentials never leave your machine</strong>. The bot reads the screen like a player would (it does not touch the game's code or memory) and you control everything from a web panel that works on any device, including your phone.</p>

<h2>Is It Safe? The Honest Answer About Bans</h2>
<p>Using any automation violates the <strong>Terms of Service of Whiteout Survival</strong> — anyone who tells you otherwise is lying to you. The realistic risk picture:</p>
<ul>
  <li>White Bot mimics human play: variable action timings, natural pauses, realistic session cycles. No injection, no memory reading — from the game's side it looks like taps.</li>
  <li>To date there is <strong>no confirmed ban</strong> among White Bot users. The risk still exists, and it is yours: run the bot on an account you accept that risk for.</li>
  <li>Established accounts doing normal actions (gathering, training, hunting) draw far less attention than fresh accounts doing inhuman 24/7 loops. White Bot is designed for established accounts only.</li>
</ul>

<h2>Setting It Up (30–40 Minutes From Zero)</h2>
<p>You need a Windows 10/11 PC that can stay on, the free BlueStacks emulator, and your existing account. In short:</p>
<ul>
  <li>Create your account at <a href="/">whitebot.app</a> — email code, no password, no card.</li>
  <li>Install BlueStacks and Whiteout Survival inside it. The game forces you through its opening tutorial (a few minutes) before you can link your account — play through it, then link your real account and you will see your own city.</li>
  <li>Install the White Bot client, activate it with your email, pick your modules on the web panel and press start.</li>
</ul>
<p>Right now White Bot is in <strong>free beta</strong>: the full bot, free, no card.</p>
<p><a href="/" class="blog-cta-inline">Try White Bot free →</a></p>

<h2>Controlling the Bot From Your Phone</h2>
<p>The control panel is a website, so your phone is a full remote: start or stop the bot, switch modules, change settings, request a live screenshot of your city and watch the action log — from work, from the bus, from bed. The bot keeps running on your PC at home; the panel is just the steering wheel. If your city gets attacked, the Auto Shield module detects it, shields up and recalls your marches without you touching anything.</p>

<h2>Pro Tip: Full Remote Control With Chrome Remote Desktop</h2>
<p>The web panel controls the <em>bot</em> from anywhere. Add <strong>Chrome Remote Desktop</strong> (free, by Google) and you also control the <em>PC and BlueStacks themselves</em> from anywhere — the missing 10%:</p>
<ul>
  <li>On the bot PC, go to <strong>remotedesktop.google.com/access</strong>, install the host and set a PIN.</li>
  <li>From your phone or any other computer, open the same page (or the mobile app), enter the PIN, and you are sitting at your PC.</li>
</ul>
<p>Why this combo is so strong: the bot handles the routine, and when something needs a human — an event you want to play manually, a weird popup, joining a rally while away from home — you remote in, play inside BlueStacks from your phone as if you were at your desk, and let the bot take over again. Between White Bot and Chrome Remote Desktop, there is nothing about your city you cannot do remotely.</p>

<h2>Strategies: Getting the Most Out of a Bot</h2>

<h3>1. Gather iron at night</h3>
<p>High-level iron tiles are contested during your server's peak hours. A bot doesn't care what time it is: let it run iron rotations overnight when tiles sit free, and switch to safer resources during peak time.</p>

<h3>2. Match training to your income</h3>
<p>Non-stop training is the biggest resource sink in the game. With gathering automated you can finally sustain continuous troop production — set training cycles and let the two loops feed each other.</p>

<h3>3. Hunt every day, save the horns</h3>
<p>Daily beast hunts cost nothing but stamina and drop the materials and chief gear resources that F2P progression depends on. During Berserk Cryptid events, White Bot spends your horns on event cryptids automatically.</p>

<h3>4. Sleep behind Auto Shield</h3>
<p>The scariest hours for any city are the ones you spend asleep. Auto Shield watches for incoming attacks, pops a shield from your backpack and recalls marching troops. It is not an absolute guarantee against instant attacks — but it turns "logged off" from your weakest state into a defended one.</p>

<h3>5. Bank ahead of events</h3>
<p>SvS and alliance events are won on stockpiles. Two weeks of 24/7 gathering before a big event is the difference between spending your savings and competing comfortably. Pair the bot with the priorities from our <a href="/blog/maximize-resources-whiteout-survival">resource guide</a>.</p>

<h3>6. Run your farm account on the bot</h3>
<p>If you keep a farm account, that is the perfect bot citizen: it only exists to gather. Let the bot run the farm 24/7 and ferry resources to your main on your schedule.</p>

<h2>Frequently Asked Questions</h2>

<h3>Is there a free Whiteout Survival bot?</h3>
<p>Macros are free and break constantly. White Bot is in free beta right now — the full bot, no card, so you never open the game at 3am to resend marches.</p>

<h3>Do I have to give the bot my game password?</h3>
<p>No. You sign in to the game yourself, inside your own BlueStacks, exactly like on your phone. White Bot never sees your game credentials — that is the point of running on YOUR PC.</p>

<h3>Does it work without a PC?</h3>
<p>You need a Windows PC (or laptop) that stays on with BlueStacks open — that is where the bot plays. Control is another story: the panel works from any phone or browser.</p>

<h3>Can I get banned for using a bot?</h3>
<p>The risk exists and you should decide with it on the table: automation violates the game's ToS. White Bot behaves like a human player and has no confirmed ban to date among its users, but no honest bot maker can promise you zero risk.</p>

<h3>Does it work with a brand-new account?</h3>
<p>No — and that is deliberate. The bot does not play tutorials or build cities from scratch. It is made for established accounts that already have marches, tiles and daily chores worth automating.</p>

<p><a href="/" class="blog-cta-inline">Join the beta at whitebot.app →</a></p>
`,
    },
    es: {
      title: 'Bot para Whiteout Survival: Guía Completa 2026 (instalación, trucos y estrategia)',
      description: 'Todo sobre usar un bot en Whiteout Survival: qué automatiza, cómo montarlo con seguridad en tu propio PC, controlarlo desde el móvil, acceso remoto con Chrome Remote Desktop y las estrategias que hacen que un bot merezca la pena.',
      keywords: 'bot whiteout survival, bot para whiteout survival, whiteout survival bot español, auto farm whiteout survival, automatizar whiteout survival, macro whiteout survival',
      body: `
<div class="blog-hero-banner blog-hero-banner-bot"><div class="blog-hero-banner-content"><span class="blog-hero-banner-tag">🤖 Guía del Bot</span><p class="blog-hero-banner-title">Tu ciudad, jugando 24/7.<br/>Todo lo que puede hacer un bot de Whiteout Survival.</p></div></div>

<p>Whiteout Survival castiga en silencio el tiempo muerto. Colas de marcha vacías, campamentos parados y bestias sin cazar son capacidad que no recuperas — y los jugadores en lo alto de tu servidor simplemente tienen menos tiempo muerto que tú. Un <strong>bot de Whiteout Survival</strong> cierra esa brecha: juega la parte repetitiva del juego (recolección, entrenamiento, caza) las 24 horas, para que tu ciudad siga creciendo mientras duermes, trabajas o estudias. Esta guía cubre cómo funcionan los bots, cómo usar uno con seguridad y las estrategias que de verdad lo rentabilizan.</p>

<div class="blog-video"><iframe src="https://www.youtube-nocookie.com/embed/dhouCxslNb4?rel=0" title="White Bot — el bot de Whiteout Survival funcionando en vivo" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>
<p class="blog-video-cap">White Bot jugando una cuenta real de Whiteout Survival — caza, recolección y el ciclo diario, controlado desde el navegador.</p>

<h2>Qué Hace Realmente un Bot de Whiteout Survival</h2>
<p>Un bot es software que juega como lo harías tú: mira la pantalla, reconoce lo que ve y pulsa. Uno bueno automatiza el bucle que se come tu día:</p>
<ul>
  <li><strong>Recolección de recursos</strong> — mantiene todas las colas de marcha en casillas de recursos, reenviando marchas según vuelven, día y noche.</li>
  <li><strong>Entrenamiento de tropas</strong> — reinicia las colas de entrenamiento en cuanto terminan.</li>
  <li><strong>Caza de bestias</strong> — gasta la energía en cazas diarias (y en críptidos de evento cuando están activos).</li>
  <li><strong>Ayuda de alianza, curación, escudos</strong> — los pequeños toques que mantienen una ciudad sana sin que tengas que vigilarla.</li>
</ul>
<p>Lo que un bot <em>no</em> hace: jugar los eventos por ti, tomar decisiones estratégicas o sustituirte en las guerras. Piénsalo como externalizar las tareas rutinarias para que tu tiempo de juego vaya a lo que necesita un humano.</p>

<h2>Tus Opciones en 2026</h2>

<h3>Macros de BlueStacks y auto-clickers genéricos</h3>
<p>Gratis, pero repiten coordenadas fijas a ciegas. Un popup, una pantalla de subida de nivel o un ataque, y la macro pulsa donde no es durante horas. Sin control remoto, sin lógica de seguridad, y se rompen con cada actualización del juego.</p>

<h3>Bots en la nube</h3>
<p>El servidor de otro juega tu cuenta — lo que significa entregar tu login a un desconocido. Ese es el mayor riesgo de este mundillo: tu cuenta ES tu progreso. Cómodo, pero le estás confiando las llaves a un tercero.</p>

<h3>Un bot de escritorio en tu propio PC (lo que es White Bot)</h3>
<p><strong><a href="/">White Bot</a></strong> corre en tu propio PC con Windows, dentro de tu propio BlueStacks. Vinculas tu cuenta del juego tú mismo, en tu emulador — <strong>tus credenciales nunca salen de tu máquina</strong>. El bot lee la pantalla como lo haría un jugador (no toca el código ni la memoria del juego) y lo controlas todo desde un panel web que funciona en cualquier dispositivo, incluido tu móvil.</p>

<h2>¿Es Seguro? La Respuesta Honesta Sobre los Baneos</h2>
<p>Usar cualquier automatización viola los <strong>Términos de Servicio de Whiteout Survival</strong> — quien te diga lo contrario te está mintiendo. El riesgo realista:</p>
<ul>
  <li>White Bot imita el juego humano: tiempos de acción variables, pausas naturales, ciclos de sesión realistas. Sin inyección ni lectura de memoria — desde el lado del juego, son pulsaciones.</li>
  <li>Hasta la fecha no hay <strong>ningún baneo confirmado</strong> entre los usuarios de White Bot. El riesgo sigue existiendo, y es tuyo: usa el bot en una cuenta con la que aceptes ese riesgo.</li>
  <li>Las cuentas establecidas haciendo acciones normales (recolectar, entrenar, cazar) llaman mucho menos la atención que cuentas nuevas en bucles inhumanos 24/7. White Bot está diseñado solo para cuentas establecidas.</li>
</ul>

<h2>Puesta en Marcha (30–40 Minutos Desde Cero)</h2>
<p>Necesitas un PC con Windows 10/11 que pueda quedarse encendido, el emulador gratuito BlueStacks y tu cuenta de siempre. En corto:</p>
<ul>
  <li>Crea tu cuenta en <a href="/">whitebot.app</a> — código por email, sin contraseña y sin tarjeta.</li>
  <li>Instala BlueStacks y Whiteout Survival dentro. El juego te obliga a jugar su tutorial inicial (unos minutos) antes de dejarte vincular la cuenta — complétalo, vincula tu cuenta real y verás tu propia ciudad.</li>
  <li>Instala el cliente de White Bot, actívalo con tu email, elige tus módulos en el panel web y pulsa iniciar.</li>
</ul>
<p>Ahora mismo White Bot está en <strong>beta gratuita</strong>: el bot completo, gratis y sin tarjeta.</p>
<p><a href="/" class="blog-cta-inline">Prueba White Bot gratis →</a></p>

<h2>Controlar el Bot Desde el Móvil</h2>
<p>El panel de control es una web, así que tu móvil es un mando a distancia completo: arranca o para el bot, cambia módulos y ajustes, pide una captura en vivo de tu ciudad y sigue el registro de actividad — desde el trabajo, el bus o la cama. El bot sigue corriendo en tu PC de casa; el panel es solo el volante. Y si atacan tu ciudad, el módulo Auto Escudo lo detecta, pone escudo y repliega tus marchas sin que toques nada.</p>

<h2>Truco Pro: Control Total con Chrome Remote Desktop</h2>
<p>El panel web controla el <em>bot</em> desde cualquier parte. Añade <strong>Chrome Remote Desktop</strong> (gratis, de Google) y controlas también el <em>PC y el propio BlueStacks</em> desde cualquier parte — el 10% que faltaba:</p>
<ul>
  <li>En el PC del bot, entra en <strong>remotedesktop.google.com/access</strong>, instala el host y pon un PIN.</li>
  <li>Desde tu móvil u otro ordenador, abre la misma página (o la app móvil), mete el PIN, y estás sentado delante de tu PC.</li>
</ul>
<p>Por qué esta combinación es tan potente: el bot se ocupa de la rutina, y cuando algo necesita un humano — un evento que quieres jugar a mano, un popup raro, unirte a una concentración estando fuera de casa — entras en remoto, juegas dentro de BlueStacks desde el móvil como si estuvieras en tu mesa, y el bot retoma el control después. Entre White Bot y Chrome Remote Desktop, no hay nada de tu ciudad que no puedas hacer a distancia.</p>

<h2>Estrategias: Sacarle Todo el Partido a un Bot</h2>

<h3>1. Recolecta hierro de noche</h3>
<p>Las casillas de hierro de nivel alto están disputadas en las horas punta de tu servidor. Al bot le da igual la hora: ponlo a rotar hierro de madrugada, cuando las casillas están libres, y cambia a recursos más seguros en hora punta.</p>

<h3>2. Ajusta el entrenamiento a tus ingresos</h3>
<p>Entrenar sin parar es el mayor sumidero de recursos del juego. Con la recolección automatizada por fin puedes sostener producción continua de tropas — configura ciclos de entrenamiento y deja que los dos bucles se alimenten entre sí.</p>

<h3>3. Caza cada día, guarda los cuernos</h3>
<p>Las cazas diarias solo cuestan energía y sueltan los materiales y recursos de equipo de jefe de los que depende la progresión F2P. Durante los eventos de Críptido Berserk, White Bot gasta tus cuernos en críptidos de evento automáticamente.</p>

<h3>4. Duerme detrás del Auto Escudo</h3>
<p>Las horas más peligrosas para cualquier ciudad son las que pasas dormido. Auto Escudo vigila los ataques entrantes, activa un escudo de tu mochila y repliega las tropas en marcha. No es una garantía absoluta contra ataques instantáneos — pero convierte el "desconectado" de tu estado más débil en uno defendido.</p>

<h3>5. Acumula antes de los eventos</h3>
<p>SvS y los eventos de alianza se ganan con reservas. Dos semanas de recolección 24/7 antes de un gran evento son la diferencia entre fundirte los ahorros y competir con holgura. Combina el bot con las prioridades de nuestra <a href="/blog/maximize-resources-whiteout-survival">guía de recursos</a>.</p>

<h3>6. Pon tu cuenta granja en el bot</h3>
<p>Si mantienes una cuenta granja, esa es la ciudadana perfecta del bot: solo existe para recolectar. Deja que el bot lleve la granja 24/7 y trasvasa recursos a tu principal cuando te convenga.</p>

<h2>Preguntas Frecuentes</h2>

<h3>¿Hay algún bot gratis para Whiteout Survival?</h3>
<p>Las macros son gratis y se rompen constantemente. White Bot está en beta gratuita ahora mismo — el bot completo, sin tarjeta, para no abrir el juego a las 3 de la mañana y reenviar marchas.</p>

<h3>¿Tengo que darle al bot mi contraseña del juego?</h3>
<p>No. Inicias sesión en el juego tú mismo, dentro de tu propio BlueStacks, igual que en tu móvil. White Bot nunca ve tus credenciales del juego — esa es la gracia de que corra en TU PC.</p>

<h3>¿Funciona sin PC?</h3>
<p>Necesitas un PC (o portátil) con Windows encendido con BlueStacks abierto — ahí es donde juega el bot. El control es otra historia: el panel funciona desde cualquier móvil o navegador.</p>

<h3>¿Me pueden banear por usar un bot?</h3>
<p>El riesgo existe y debes decidir con él sobre la mesa: la automatización viola los ToS del juego. White Bot se comporta como un jugador humano y no tiene ningún baneo confirmado hasta la fecha entre sus usuarios, pero ningún creador de bots honesto puede prometerte riesgo cero.</p>

<h3>¿Funciona con una cuenta recién creada?</h3>
<p>No — y es deliberado. El bot no juega tutoriales ni construye ciudades desde cero. Está hecho para cuentas establecidas que ya tienen marchas, casillas y tareas diarias que merezca la pena automatizar.</p>

<p><a href="/" class="blog-cta-inline">Únete a la beta en whitebot.app →</a></p>
`,
    },
  },
  {
    slug: 'maximize-resources-whiteout-survival',
    publishedAt: '2025-05-15',
    readingMinutes: 7,
    en: {
      title: 'How to Maximize Resources in Whiteout Survival (2025 Guide)',
      description: 'Complete guide to maximizing food, wood, coal and iron in Whiteout Survival. Proven strategies, research priorities and automation tips to grow your city faster.',
      keywords: 'whiteout survival resources, how to get resources whiteout survival, whiteout survival tips, whiteout survival gathering guide',
      body: `
<div class="blog-hero-banner blog-hero-banner-resources"><div class="blog-hero-banner-content"><span class="blog-hero-banner-tag">📦 Resources Guide</span><p class="blog-hero-banner-title">Food. Wood. Coal. Iron.<br/>Master all four. Outgrow everyone.</p></div></div>

<p>Resources are the foundation of everything in <strong>Whiteout Survival</strong>. Every troop you train, every building you upgrade, every research you unlock — it all comes down to how efficiently you gather food, wood, coal, and iron. In this guide we cover the best strategies to maximize your resource income, whether you play casually or competitively.</p>

<h2>Why Resources Define Your Progress</h2>
<p>Unlike many mobile strategy games, Whiteout Survival puts a hard cap on your daily progression through resource availability. Players who maintain full gathering queues and minimise downtime consistently outpace players with similar VIP levels and spending. The gap between an optimised gatherer and a passive player compounds daily.</p>

<h2>The 4 Core Resources — and How Each Works</h2>

<h3>Food</h3>
<p>Food is consumed continuously by your troops. Running out triggers a happiness penalty that reduces troop combat power. Keep food production ahead of consumption at all times, especially after large training sessions.</p>

<h3>Wood</h3>
<p>Wood is the most universally required resource for early and mid-game construction and research. Prioritise wood gathering until your city reaches Furnace level 15+.</p>

<h3>Coal</h3>
<p>Coal becomes critical from mid-game onwards. It gates most advanced research and higher-tier troop training. Players who neglect coal early often hit a hard wall at Furnace level 16–18.</p>

<h3>Iron</h3>
<p>Iron is the late-game premium resource. Required for T4 and T5 troops, advanced gear, and top-tier research. Iron tiles on the world map are contested — timing your marches matters.</p>

<h2>5 Proven Strategies to Gather Resources Faster</h2>

<h3>1. Never Let Your March Queues Sit Empty</h3>
<p>Each idle march queue is wasted gathering capacity. With 3 march queues active for 8 hours, you can complete 24 march-hours of gathering per day. With only 1 queue active, you lose 66% of that capacity. Always send all available marches before you log off.</p>

<h3>2. Prioritise Gathering Research First</h3>
<p>The Gathering branch in the Research tree offers some of the highest ROI of any early investment. Increasing march load capacity by 20–40% effectively multiplies every gathering session you do for the rest of the game. Complete this branch before focusing on combat research.</p>

<h3>3. Use Gathering Gear</h3>
<p>Craft and equip a dedicated gathering gear set. A full gathering set can increase march load by 30–60% depending on rarity. Many players keep a combat set equipped and forget to switch — this single habit change can double your effective yield per march.</p>

<h3>4. Participate in Alliance Help and Events</h3>
<p>Alliance members can speed up construction and research, freeing up your time budget for active gathering. Alliance events often reward bonus resources directly — treat them as additional gathering capacity, not optional bonuses.</p>

<h3>5. Identify and Target High-Yield Tiles</h3>
<p>Higher-level resource tiles yield more per hour. On the world map, prioritise level 4 and 5 tiles over lower-level ones when march load allows. Scout before sending — contested tiles lose yield when multiple players compete.</p>

<h2>The Hidden Cost: Downtime</h2>
<p>The strategies above assume you're actively managing the game. The real constraint for most players isn't strategy — it's time. A player who follows all five strategies but only plays 1 hour a day will have their march queues sitting empty for 23 hours. That's a 96% idle rate on their gathering capacity.</p>
<p>Consider this: if your three march queues can gather 15,000 iron per march (roughly 2 hours per round trip), you could theoretically gather <strong>270,000 iron in 24 hours</strong>. Most players manage 30,000–45,000 because their queues sit empty overnight.</p>

<h2>Gathering 24/7 with White Bot</h2>
<p><strong>White Bot</strong> runs on your own Windows PC (inside BlueStacks) and keeps your Whiteout Survival gathering queues active around the clock — you control it from the web, from any device.</p>
<p>The bot handles resource gathering, troop training queues, and monster hunting automatically. You configure which actions to run from the web panel, and the bot executes them continuously on your machine — your account credentials never leave your PC.</p>
<ul>
  <li>Runs on your PC — you install it, you own it</li>
  <li>Works while you sleep, work, or study</li>
  <li>Full control from any browser, including mobile</li>
  <li>Designed for established accounts — not for new accounts or tutorials</li>
</ul>
<p>Want the full picture? Read our <a href="/blog/whiteout-survival-bot-guide">complete Whiteout Survival bot guide</a>.</p>
<p><a href="/" class="blog-cta-inline">Try White Bot free →</a></p>

<h2>Frequently Asked Questions</h2>

<h3>How many marches should I send for gathering?</h3>
<p>Always send the maximum available march count. More queues active simultaneously means more total yield. Research and VIP levels that unlock additional march queues are high-priority upgrades.</p>

<h3>Should I gather inside or outside my alliance territory?</h3>
<p>Inside alliance territory you gain protection bonuses. For contested resources like high-level iron tiles, gathering outside is necessary but carry enough troops to deter reinforcements.</p>

<h3>When is the best time to gather?</h3>
<p>During non-peak hours in your server's dominant alliance timezone. This reduces march interruption risk. If you're automating gathering, this concern largely disappears since the bot can react and resend interrupted marches.</p>

<h3>Does troop load affect gathering speed?</h3>
<p>Load capacity (affected by march load research and gathering gear) determines how much you bring back per trip, but does not affect gathering speed per se. More load = fewer trips needed for the same total yield.</p>
`,
    },
    es: {
      title: 'Cómo Maximizar Recursos en Whiteout Survival (Guía 2025)',
      description: 'Guía completa para maximizar comida, madera, carbón y hierro en Whiteout Survival. Estrategias probadas, prioridades de investigación y automatización para crecer más rápido.',
      keywords: 'recursos whiteout survival, como conseguir recursos whiteout survival, trucos whiteout survival, guia recoleccion whiteout survival',
      body: `
<div class="blog-hero-banner blog-hero-banner-resources"><div class="blog-hero-banner-content"><span class="blog-hero-banner-tag">📦 Guía de Recursos</span><p class="blog-hero-banner-title">Comida. Madera. Carbón. Hierro.<br/>Domínalos todos. Supera a todos.</p></div></div>

<p>Los recursos son la base de todo en <strong>Whiteout Survival</strong>. Cada tropa que entrenas, cada edificio que mejoras, cada investigación que desbloqueas: todo depende de cuán eficientemente recolectas comida, madera, carbón y hierro. En esta guía cubrimos las mejores estrategias para maximizar tu producción de recursos, tanto si juegas de forma casual como competitiva.</p>

<h2>Por Qué los Recursos Definen tu Progreso</h2>
<p>A diferencia de muchos juegos de estrategia móvil, Whiteout Survival impone un límite duro en tu progresión diaria a través de la disponibilidad de recursos. Los jugadores que mantienen las colas de recolección llenas y minimizan el tiempo muerto superan consistentemente a jugadores con niveles VIP y gasto similares. La diferencia entre un recolector optimizado y un jugador pasivo se multiplica cada día.</p>

<h2>Los 4 Recursos Principales — Cómo Funciona Cada Uno</h2>

<h3>Comida</h3>
<p>La comida la consumen continuamente tus tropas. Quedarse sin ella activa una penalización de felicidad que reduce el poder de combate. Mantén la producción de comida por delante del consumo en todo momento, especialmente tras grandes sesiones de entrenamiento.</p>

<h3>Madera</h3>
<p>La madera es el recurso más universalmente necesario para construcción e investigación en las fases tempranas y medias. Prioriza la recolección de madera hasta que tu ciudad alcance el nivel 15+ del Horno.</p>

<h3>Carbón</h3>
<p>El carbón se vuelve crítico a partir de la fase media del juego. Desbloquea la mayoría de las investigaciones avanzadas y el entrenamiento de tropas de nivel superior. Los jugadores que descuidan el carbón al principio suelen chocar contra un muro en el nivel 16–18 del Horno.</p>

<h3>Hierro</h3>
<p>El hierro es el recurso premium de la fase tardía. Es necesario para tropas T4 y T5, equipo avanzado e investigaciones de primer nivel. Las casillas de hierro en el mapa mundial están disputadas — el momento en que envías tus marchas importa.</p>

<h2>5 Estrategias Probadas para Recolectar Recursos Más Rápido</h2>

<h3>1. Nunca Dejes las Colas de Marcha Vacías</h3>
<p>Cada cola de marcha inactiva es capacidad de recolección desperdiciada. Con 3 colas de marcha activas durante 8 horas, puedes completar 24 horas-marcha de recolección al día. Con solo 1 cola activa, pierdes el 66% de esa capacidad. Siempre envía todas las marchas disponibles antes de cerrar sesión.</p>

<h3>2. Prioriza la Investigación de Recolección</h3>
<p>La rama de Recolección en el árbol de Investigación ofrece uno de los mayores retornos de cualquier inversión temprana. Aumentar la capacidad de carga de marcha un 20–40% multiplica efectivamente cada sesión de recolección para el resto del juego. Completa esta rama antes de centrarte en la investigación de combate.</p>

<h3>3. Usa Equipo de Recolección</h3>
<p>Fabrica y equipa un set de equipo dedicado a la recolección. Un set completo puede aumentar la carga de marcha un 30–60% según la rareza. Muchos jugadores mantienen el set de combate equipado y olvidan cambiarlo — este solo cambio de hábito puede duplicar el rendimiento por marcha.</p>

<h3>4. Participa en la Ayuda de Alianza y Eventos</h3>
<p>Los miembros de la alianza pueden acelerar construcción e investigación, liberando tu presupuesto de tiempo para la recolección activa. Los eventos de alianza recompensan recursos adicionales directamente — trátalos como capacidad de recolección extra, no como bonificaciones opcionales.</p>

<h3>5. Identifica y Apunta a Casillas de Alto Rendimiento</h3>
<p>Las casillas de recursos de nivel superior producen más por hora. En el mapa mundial, prioriza las casillas de nivel 4 y 5 sobre las de nivel inferior cuando la carga de marcha lo permita. Explora antes de enviar — las casillas disputadas pierden rendimiento cuando varios jugadores compiten.</p>

<h2>El Costo Oculto: el Tiempo Muerto</h2>
<p>Las estrategias anteriores asumen que gestionas activamente el juego. La restricción real para la mayoría de los jugadores no es la estrategia, sino el tiempo. Un jugador que sigue las cinco estrategias pero solo juega 1 hora al día tendrá las colas de marcha vacías durante 23 horas. Eso es un 96% de tiempo muerto en su capacidad de recolección.</p>
<p>Considera esto: si tus tres colas de marcha pueden recolectar 15.000 de hierro por marcha (aproximadamente 2 horas por viaje de ida y vuelta), teóricamente podrías recolectar <strong>270.000 de hierro en 24 horas</strong>. La mayoría de los jugadores consigue 30.000–45.000 porque sus colas permanecen vacías durante la noche.</p>

<h2>Recolección 24/7 con White Bot</h2>
<p><strong>White Bot</strong> corre en tu propio PC con Windows (dentro de BlueStacks) y mantiene las colas de recolección de Whiteout Survival activas las 24 horas del día — lo controlas desde la web, desde cualquier dispositivo.</p>
<p>El bot gestiona la recolección de recursos, las colas de entrenamiento de tropas y la caza de monstruos automáticamente. Configuras qué acciones ejecutar desde el panel web, y el bot las ejecuta de forma continua en tu máquina — tus credenciales de la cuenta nunca salen de tu PC.</p>
<ul>
  <li>Corre en tu PC — tú lo instalas, es tuyo</li>
  <li>Funciona mientras duermes, trabajas o estudias</li>
  <li>Control completo desde cualquier navegador, incluyendo el móvil</li>
  <li>Diseñado para cuentas ya establecidas — no para cuentas nuevas ni tutoriales</li>
</ul>
<p>¿Quieres la foto completa? Lee nuestra <a href="/blog/whiteout-survival-bot-guide">guía completa del bot de Whiteout Survival</a>.</p>
<p><a href="/" class="blog-cta-inline">Prueba White Bot gratis →</a></p>

<h2>Preguntas Frecuentes</h2>

<h3>¿Cuántas marchas debo enviar para recolectar?</h3>
<p>Siempre envía el máximo de marchas disponibles. Cuantas más colas activas simultáneamente, mayor el rendimiento total. Las mejoras de investigación y VIP que desbloquean colas de marcha adicionales son actualizaciones de alta prioridad.</p>

<h3>¿Debo recolectar dentro o fuera del territorio de la alianza?</h3>
<p>Dentro del territorio de la alianza obtienes bonificaciones de protección. Para recursos disputados como casillas de hierro de nivel alto, recolectar fuera es necesario, pero lleva suficientes tropas para disuadir refuerzos.</p>

<h3>¿Cuál es el mejor momento para recolectar?</h3>
<p>Durante las horas de menor actividad en la zona horaria de la alianza dominante de tu servidor. Esto reduce el riesgo de interrupción de marchas. Si automatizas la recolección, esta preocupación desaparece en gran medida.</p>

<h3>¿Afecta la carga de tropas a la velocidad de recolección?</h3>
<p>La capacidad de carga (afectada por la investigación de carga de marcha y el equipo de recolección) determina cuánto traes de vuelta por viaje, pero no afecta a la velocidad de recolección en sí. Más carga = menos viajes necesarios para el mismo rendimiento total.</p>
`,
    },
  },
  // ─────────────────────────────────────────────
  // ARTICLE 2: BEGINNER'S GUIDE
  // ─────────────────────────────────────────────
  {
    slug: 'whiteout-survival-beginners-guide',
    publishedAt: '2025-05-22',
    readingMinutes: 11,
    en: {
      title: 'Whiteout Survival: Complete Beginner\'s Guide (2025)',
      description: 'Everything you need to know to start strong in Whiteout Survival. Furnace priorities, first heroes, alliances, and the 10 mistakes that kill new players.',
      keywords: 'whiteout survival beginner guide, whiteout survival tips for beginners, how to start whiteout survival, whiteout survival furnace guide',
      body: `
<div class="blog-hero-banner blog-hero-banner-beginner"><div class="blog-hero-banner-content"><span class="blog-hero-banner-tag">📖 Beginner Guide</span><p class="blog-hero-banner-title">Start strong. Build smart.<br/>Dominate from day one.</p></div></div>

<p>Whiteout Survival drops you into a frozen world with a furnace on the verge of dying out and a city full of survivors counting on you. The first two weeks define your trajectory for months. This guide covers everything you need to start fast and avoid the classic mistakes that stall most new players.</p>

<div class="blog-stat-row">
  <div class="blog-stat-box"><span class="bsb-num">30</span><span class="bsb-label">Furnace max level</span></div>
  <div class="blog-stat-box"><span class="bsb-num">5</span><span class="bsb-label">Free trial days</span></div>
  <div class="blog-stat-box"><span class="bsb-num">3</span><span class="bsb-label">Troop types</span></div>
  <div class="blog-stat-box"><span class="bsb-num">4</span><span class="bsb-label">Resources to manage</span></div>
</div>

<h2>1. The Furnace: Everything Starts Here</h2>
<p>The Furnace is the single most important building in the game. Its level acts as a hard cap on every other building — your Embassy, Research Center, troop camps, infirmary, all of them are locked until your Furnace reaches the required level. No furnace = no progress.</p>

<div class="blog-callout">
  <strong>Key rule</strong>
  Always have the Furnace upgrading or a building that directly unlocks the next Furnace level. Every idle hour is progress permanently lost.
</div>

<p>The Furnace also heats your city. Without enough warmth, survivors fall ill and stop working — halting all resource production. Keep the furnace fueled with coal at all times, especially during blizzard events when fuel consumption doubles.</p>

<h3>Furnace fuel tips</h3>
<ul class="blog-checklist">
  <li>Stockpile at least 24 hours of coal before starting long upgrades</li>
  <li>Toggle Max Mode OFF during daytime (saves 50% coal)</li>
  <li>Toggle Max Mode ON during blizzards (required to maintain heat)</li>
  <li class="bad">Never let the furnace go cold — survivors stop producing instantly</li>
</ul>

<h2>2. Join an Alliance Immediately</h2>
<p>Whiteout Survival is fundamentally a multiplayer game. Solo players are raided, miss events, and grow 3–5× slower than active alliance members. Your very first action after the tutorial should be joining an established alliance.</p>

<div class="blog-callout blog-callout-success">
  <strong>What alliances give you</strong>
  Speed-ups from help requests · 20% gathering bonus inside territory · Protection from raids · Access to Bear Hunt, SvS, and alliance events · Shared resources and knowledge
</div>

<p>Find an alliance in your furnace level range. A top-50 alliance that ignores you is worse than a mid-tier alliance that actively helps. Alliance activity level matters more than rank.</p>

<h2>3. Your First Heroes — Focus, Don't Spread</h2>
<p>The biggest mistake new players make is leveling every hero they get. Hero development costs skill manuals and shards — both are scarce. Spreading them thin means you'll have six mediocre heroes instead of two strong ones.</p>

<div class="blog-section-banner blog-section-banner-dark">🦸 🗡️ ⚔️</div>

<h3>F2P hero priority path</h3>
<ol class="blog-step-list">
  <li><strong>Mia</strong> — Your starting healer. Level her first, she keeps your troops alive in every early fight.</li>
  <li><strong>Hector</strong> — Strong infantry tank. Pairs perfectly with Mia for early PvE.</li>
  <li><strong>Bradley</strong> — Ranged damage. Unlocks through daily quests.</li>
  <li><strong>Gatot / Blanchette</strong> — Mid-game power spike. Blanchette is free through Lucky Wheel if you're patient.</li>
</ol>

<div class="blog-callout blog-callout-warn">
  <strong>Gathering heroes (Cloris, Eugene, Smith, Charlie)</strong>
  These R-tier heroes only need their gathering skill unlocked — do NOT invest skill manuals in their combat skills. They exist to farm resources, not to fight.
</div>

<h2>4. Research: The Silent Multiplier</h2>
<p>Research provides permanent bonuses that compound over time. A player who keeps research running 24/7 will outpace a bigger spender who leaves the queue idle. Always have something researching.</p>

<div class="blog-table-wrap">
  <table>
    <tr><th>Tree</th><th>Priority</th><th>Key researches</th></tr>
    <tr><td>Growth</td><td><span class="blog-tier-s">First</span></td><td>Tool Enhancement VII, Construction Speed, March Size</td></tr>
    <tr><td>Battle</td><td><span class="blog-tier-a">Second</span></td><td>Infantry Defense, Survival Training, Command Tactics</td></tr>
    <tr><td>Economy</td><td><span class="blog-tier-b">Third</span></td><td>Coal Gathering, Iron Gathering (critical late-game)</td></tr>
  </table>
</div>

<h2>5. Building Upgrade Priority</h2>
<p>Not all buildings are equal. Some unlock critical capabilities, others are nice-to-haves. Follow this sequence:</p>

<ol class="blog-step-list">
  <li><strong>Furnace</strong> — Always the primary objective. Everything else follows.</li>
  <li><strong>Embassy</strong> — Alternate with Furnace. Enables reinforcements and increases help frequency from alliance members.</li>
  <li><strong>Infirmary</strong> — Wounded troops are cheaper to heal than retrain. Capacity matters.</li>
  <li><strong>Resource buildings</strong> — Sawmill, Meat Farm, Coal Mine, Iron Mine. Keep them at Furnace level or near.</li>
  <li><strong>Warehouse</strong> — Protects resources from raids. Don't neglect it once you start PvP.</li>
</ol>

<h2>6. The 10 Mistakes That Kill New Players</h2>

<ul class="blog-checklist">
  <li class="bad">Letting the furnace sit idle — one idle day = days of lost progress</li>
  <li class="bad">Leveling all heroes equally — wastes scarce manuals and shards</li>
  <li class="bad">Burning speed-ups on 5-minute tasks — save them for 6+ hour upgrades</li>
  <li class="bad">Playing without an alliance — you'll be raided and starved of events</li>
  <li class="bad">Skipping the Warehouse — your resources are exposed to any attacker</li>
  <li class="bad">Running research only when you remember — queue it before you sleep</li>
  <li class="bad">Ignoring survivor morale — low mood = reduced production across the city</li>
  <li class="bad">Missing Bear Hunt — it's the best source of Hardened Alloys in the game</li>
  <li class="bad">Upgrading troops before researching — research first, train later</li>
  <li class="bad">Not shielding before going offline in hostile territory</li>
</ul>

<h2>7. Speed-ups: Use Them Wisely</h2>
<p>Speed-ups are the most valuable resource in the game. Early players who burn them on small tasks regret it at Furnace 20+ when upgrades cost 3–7 days each.</p>

<div class="blog-callout">
  <strong>The rule of thumb</strong>
  Only use speed-ups on projects over 3 hours that are actively blocking your next Furnace upgrade. Let everything else complete naturally.
</div>

<p>The only exception: event alignment. If completing a build during an event gives you bonus rewards, it can be worth spending speed-ups to time the finish.</p>

<h2>8. Automate the Boring Parts</h2>
<p>The biggest bottleneck for most players isn't strategy — it's time. Gathering troops sitting idle overnight, bear hunt sessions you sleep through, and resource queues that run dry at 3am. This is precisely what <a href="/">White Bot</a> handles: continuous gathering, auto-joining rallies, and keeping your queues full 24/7 while you're offline.</p>

<div class="blog-callout blog-callout-success">
  <strong>What automation changes</strong>
  Players using continuous automation for gathering alone report 40–60% faster resource accumulation versus manual play over a 30-day period — without spending more on the game.
</div>
`,
    },
    es: {
      title: 'Guía Completa para Principiantes en Whiteout Survival (2025)',
      description: 'Todo lo que necesitas para empezar con fuerza en Whiteout Survival. Prioridades del horno, primeros héroes, alianzas y los 10 errores que hunden a los nuevos jugadores.',
      keywords: 'guía principiantes whiteout survival, consejos whiteout survival, cómo empezar whiteout survival, guía horno whiteout survival',
      body: `
<div class="blog-hero-banner blog-hero-banner-beginner"><div class="blog-hero-banner-content"><span class="blog-hero-banner-tag">📖 Guía Principiantes</span><p class="blog-hero-banner-title">Empieza fuerte. Construye con cabeza.<br/>Domina desde el primer día.</p></div></div>

<p>Whiteout Survival te lanza a un mundo helado con un horno al borde de apagarse y una ciudad llena de supervivientes que dependen de ti. Las primeras dos semanas definen tu trayectoria durante meses. Esta guía cubre todo lo necesario para arrancar rápido y evitar los errores clásicos que frenan a la mayoría de jugadores nuevos.</p>

<div class="blog-stat-row">
  <div class="blog-stat-box"><span class="bsb-num">30</span><span class="bsb-label">Nivel máximo del horno</span></div>
  <div class="blog-stat-box"><span class="bsb-num">5</span><span class="bsb-label">Días de prueba gratis</span></div>
  <div class="blog-stat-box"><span class="bsb-num">3</span><span class="bsb-label">Tipos de tropas</span></div>
  <div class="blog-stat-box"><span class="bsb-num">4</span><span class="bsb-label">Recursos a gestionar</span></div>
</div>

<h2>1. El Horno: Todo Empieza Aquí</h2>
<p>El Horno es el edificio más importante del juego. Su nivel actúa como límite duro para todos los demás edificios — Embajada, Centro de Investigación, barracones, enfermería... todos están bloqueados hasta que el Horno alcanza el nivel requerido. Sin horno no hay progreso.</p>

<div class="blog-callout">
  <strong>Regla clave</strong>
  El Horno debe estar siempre en mejora, o debe estar mejorando el edificio que desbloquea el siguiente nivel del Horno. Cada hora de inactividad es progreso perdido para siempre.
</div>

<p>El Horno también calienta tu ciudad. Sin suficiente calor, los supervivientes enferman y dejan de trabajar, paralizando toda la producción de recursos. Mantén siempre el horno con carbón, especialmente durante las tormentas donde el consumo se duplica.</p>

<h3>Consejos para el combustible del horno</h3>
<ul class="blog-checklist">
  <li>Acumula al menos 24 horas de carbón antes de iniciar mejoras largas</li>
  <li>Desactiva el Modo Máximo durante el día (ahorra 50% de carbón)</li>
  <li>Activa el Modo Máximo durante tormentas (necesario para mantener el calor)</li>
  <li class="bad">Nunca dejes que el horno se apague — los supervivientes dejan de producir de inmediato</li>
</ul>

<h2>2. Únete a una Alianza Inmediatamente</h2>
<p>Whiteout Survival es en su esencia un juego multijugador. Los jugadores en solitario son saqueados, pierden eventos y crecen 3–5 veces más lento que los miembros activos de una alianza. Tu primera acción tras el tutorial debe ser unirte a una alianza establecida.</p>

<div class="blog-callout blog-callout-success">
  <strong>Lo que te da una alianza</strong>
  Aceleraciones de ayudas · Bono de recolección del 20% en territorio · Protección de saqueos · Acceso a Caza del Oso, SvS y eventos de alianza · Recursos y conocimiento compartidos
</div>

<h2>3. Tus Primeros Héroes — Enfoca, No Disperses</h2>
<p>El mayor error de los nuevos jugadores es subir de nivel a todos los héroes que consiguen. El desarrollo de héroes consume manuales de habilidades y fragmentos — ambos son escasos. Distribuirlos deja seis héroes mediocres en vez de dos fuertes.</p>

<div class="blog-section-banner blog-section-banner-dark">🦸 🗡️ ⚔️</div>

<h3>Ruta de héroes F2P prioritaria</h3>
<ol class="blog-step-list">
  <li><strong>Mia</strong> — Tu sanadora de inicio. Súbela primero, mantiene vivas tus tropas en cada combate temprano.</li>
  <li><strong>Héctor</strong> — Tanque de infantería resistente. Se combina perfectamente con Mia.</li>
  <li><strong>Bradley</strong> — Daño a distancia. Se desbloquea con misiones diarias.</li>
  <li><strong>Gatot / Blanchette</strong> — Salto de poder a mitad de partida. Blanchette es gratuita con la Rueda de la Suerte si eres paciente.</li>
</ol>

<div class="blog-callout blog-callout-warn">
  <strong>Héroes recolectores (Cloris, Eugene, Smith, Charlie)</strong>
  Estos héroes de rareza R solo necesitan desbloquear su habilidad de recolección. NO inviertas manuales en sus habilidades de combate. Existen para farmear recursos, no para pelear.
</div>

<h2>4. Investigación: El Multiplicador Silencioso</h2>
<p>La investigación proporciona bonificaciones permanentes que se acumulan con el tiempo. Un jugador que mantiene la cola de investigación activa 24/7 superará a un mayor inversor que la deja inactiva. Siempre debe haber algo investigándose.</p>

<div class="blog-table-wrap">
  <table>
    <tr><th>Árbol</th><th>Prioridad</th><th>Investigaciones clave</th></tr>
    <tr><td>Crecimiento</td><td><span class="blog-tier-s">Primero</span></td><td>Mejora de Herramientas VII, Velocidad de Construcción, Tamaño de Marcha</td></tr>
    <tr><td>Batalla</td><td><span class="blog-tier-a">Segundo</span></td><td>Defensa de Infantería, Entrenamiento de Supervivencia, Tácticas de Mando</td></tr>
    <tr><td>Economía</td><td><span class="blog-tier-b">Tercero</span></td><td>Recolección de Carbón, Recolección de Hierro (crítico en partida avanzada)</td></tr>
  </table>
</div>

<h2>5. Prioridad de Mejora de Edificios</h2>
<ol class="blog-step-list">
  <li><strong>Horno</strong> — Siempre el objetivo principal. Todo lo demás lo sigue.</li>
  <li><strong>Embajada</strong> — Alternar con el Horno. Activa refuerzos y aumenta la frecuencia de ayudas.</li>
  <li><strong>Enfermería</strong> — Las tropas heridas son más baratas de curar que de reclutar. La capacidad importa.</li>
  <li><strong>Edificios de recursos</strong> — Aserradero, Granja, Mina de Carbón, Mina de Hierro. Mantenerlos al nivel del Horno.</li>
  <li><strong>Almacén</strong> — Protege recursos de saqueos. No lo descuides cuando empiece el PvP.</li>
</ol>

<h2>6. Los 10 Errores que Hunden a los Nuevos Jugadores</h2>

<ul class="blog-checklist">
  <li class="bad">Dejar el horno parado — un día inactivo equivale a días de progreso perdido</li>
  <li class="bad">Subir de nivel a todos los héroes por igual — malgasta manuales y fragmentos escasos</li>
  <li class="bad">Quemar aceleraciones en tareas de 5 minutos — guárdalas para mejoras de +6 horas</li>
  <li class="bad">Jugar sin alianza — serás saqueado y te perderás los eventos</li>
  <li class="bad">Ignorar el Almacén — tus recursos quedan expuestos a cualquier atacante</li>
  <li class="bad">Investigar solo cuando te acuerdas — ponla en marcha antes de dormir</li>
  <li class="bad">Ignorar la moral de los supervivientes — ánimo bajo = producción reducida en toda la ciudad</li>
  <li class="bad">Perderte la Caza del Oso — es la mejor fuente de Aleaciones Endurecidas del juego</li>
  <li class="bad">Entrenar tropas antes de investigar — primero investiga, luego entrena</li>
  <li class="bad">No poner escudo antes de desconectarte en territorio hostil</li>
</ul>

<h2>7. Aceleraciones: Úsalas con Cabeza</h2>

<div class="blog-callout">
  <strong>La regla básica</strong>
  Usa aceleraciones solo en proyectos de más de 3 horas que estén bloqueando activamente tu próxima mejora del Horno. Deja que todo lo demás se complete de forma natural.
</div>

<h2>8. Automatiza las Partes Aburridas</h2>
<p>El mayor cuello de botella para la mayoría de jugadores no es la estrategia — es el tiempo. Tropas de recolección inactivas toda la noche, sesiones de Caza del Oso que te pierdes mientras duermes, y colas de recursos que se vacían a las 3am. Exactamente eso es lo que <a href="/">White Bot</a> gestiona: recolección continua, unirse automáticamente a rallies, y mantener tus colas llenas 24/7 mientras estás desconectado.</p>

<div class="blog-callout blog-callout-success">
  <strong>Lo que cambia la automatización</strong>
  Los jugadores que usan automatización continua para recolección reportan una acumulación de recursos un 40–60% más rápida frente al juego manual durante 30 días, sin gastar más en el juego.
</div>
`,
    },
  },

  // ─────────────────────────────────────────────
  // ARTICLE 3: HERO TIER LIST
  // ─────────────────────────────────────────────
  {
    slug: 'whiteout-survival-hero-tier-list',
    publishedAt: '2025-05-29',
    readingMinutes: 9,
    en: {
      title: 'Whiteout Survival Hero Tier List 2025 — Best Heroes Ranked',
      description: 'Complete hero tier list for Whiteout Survival. Best heroes for combat, bear hunt, gathering and rally. F2P path included.',
      keywords: 'whiteout survival hero tier list, best heroes whiteout survival, whiteout survival Jessie, whiteout survival F2P heroes',
      body: `
<div class="blog-hero-banner blog-hero-banner-heroes"><div class="blog-hero-banner-content"><span class="blog-hero-banner-tag">⚔️ Hero Guide</span><p class="blog-hero-banner-title">Choose the right heroes.<br/>Invest once. Win forever.</p></div></div>

<p>Choosing the right heroes in Whiteout Survival is the single decision that most separates fast-growing players from those who plateau. Unlike buildings and research, hero development is hard to reverse — skill manuals and shards spent on the wrong heroes are gone. This tier list covers every major hero category to help you invest wisely.</p>

<div class="blog-callout">
  <strong>How heroes work</strong>
  Each hero has two separate skill sets: Exploration (PvE map content) and Expedition (city production). Leveling one does NOT improve the other. Rally joiners only contribute their top-right skill — so that single skill can define a hero's value.
</div>

<h2>S-Tier: Build These First</h2>

<div class="blog-table-wrap">
  <table>
    <tr><th>Hero</th><th>Tier</th><th>Role</th><th>Why they're essential</th></tr>
    <tr>
      <td><strong>Jessie</strong></td>
      <td><span class="blog-tier-s">S+</span></td>
      <td>Rally Leader</td>
      <td>Best Bear Hunt damage in the game. Percentage-based damage buff scales better than any alternative. Non-negotiable for rally leaders.</td>
    </tr>
    <tr>
      <td><strong>Jeronimo</strong></td>
      <td><span class="blog-tier-s">S+</span></td>
      <td>Offense / PvP</td>
      <td>Unique passive: buffs account-wide Troop Attack and Defense even when not deployed. Every battle benefits from him being leveled.</td>
    </tr>
    <tr>
      <td><strong>Patrick</strong></td>
      <td><span class="blog-tier-s">S</span></td>
      <td>Defense / Garrison</td>
      <td>Best defensive hero. Passive troop healing, boosts troop health. Essential for garrison defense and server kill events.</td>
    </tr>
    <tr>
      <td><strong>Bahiti</strong></td>
      <td><span class="blog-tier-s">S</span></td>
      <td>Combat / Rally</td>
      <td>Exceptional single-target damage. Beast hunting specialist. Top-right skill is one of the best for rally joiners.</td>
    </tr>
  </table>
</div>

<h2>A-Tier: Strong All-Rounders</h2>

<div class="blog-table-wrap">
  <table>
    <tr><th>Hero</th><th>Tier</th><th>Best use</th></tr>
    <tr><td><strong>Gina</strong></td><td><span class="blog-tier-a">A</span></td><td>PvE beast hunting — reduces stamina cost by up to 20%, saving thousands of gems</td></tr>
    <tr><td><strong>Molly</strong></td><td><span class="blog-tier-a">A</span></td><td>Reliable rally joiner, strong march attack bonuses</td></tr>
    <tr><td><strong>Natalia</strong></td><td><span class="blog-tier-a">A</span></td><td>Lancer specialist, excellent for mixed troop compositions</td></tr>
    <tr><td><strong>Zinman</strong></td><td><span class="blog-tier-a">A</span></td><td>Infantry defense anchor, pairs well with Patrick for garrison builds</td></tr>
  </table>
</div>

<div class="blog-section-banner blog-section-banner-dark">🏆 📊 🎯</div>

<h2>B-Tier: Solid but Replaceable</h2>

<div class="blog-table-wrap">
  <table>
    <tr><th>Hero</th><th>Tier</th><th>Notes</th></tr>
    <tr><td><strong>Bradley</strong></td><td><span class="blog-tier-b">B</span></td><td>Good early combat hero, free through progression. Falls behind at mid-game.</td></tr>
    <tr><td><strong>Hector</strong></td><td><span class="blog-tier-b">B</span></td><td>Reliable infantry tank for new players. Replaced by S-tier at mid-game.</td></tr>
    <tr><td><strong>Blanchette</strong></td><td><span class="blog-tier-b">B+</span></td><td>Free via Lucky Wheel. Outperforms her tier when maxed.</td></tr>
    <tr><td><strong>Eleonora</strong></td><td><span class="blog-tier-b">B</span></td><td>Healer hybrid with decent expedition skills.</td></tr>
  </table>
</div>

<h2>Gathering Heroes: A Category of Their Own</h2>

<p>These heroes are rated purely on gathering efficiency, not combat. Don't judge them by their combat skills — level only their gathering skill line.</p>

<div class="blog-table-wrap">
  <table>
    <tr><th>Hero</th><th>Resource</th><th>Priority</th></tr>
    <tr><td><strong>Eugene</strong></td><td>Wood</td><td><span class="blog-tier-s">Essential</span> — wood is the #1 most consumed resource</td></tr>
    <tr><td><strong>Cloris</strong></td><td>Meat</td><td><span class="blog-tier-a">High</span> — troops eat constantly</td></tr>
    <tr><td><strong>Smith</strong></td><td>Iron</td><td><span class="blog-tier-b">Mid</span> — iron becomes critical after Furnace 20</td></tr>
    <tr><td><strong>Charlie</strong></td><td>Mixed</td><td><span class="blog-tier-c">Flex</span> — versatile backup gatherer</td></tr>
  </table>
</div>

<div class="blog-callout blog-callout-warn">
  <strong>Gathering hero trap</strong>
  Many new players level gathering heroes' combat skills "just in case." This is a waste. Keep them at the minimum level to unlock their gathering skill. Full stop.
</div>

<h2>F2P Hero Investment Roadmap</h2>

<ol class="blog-step-list">
  <li><strong>Week 1–2:</strong> Mia to max. She's free and keeps you alive through every early event.</li>
  <li><strong>Week 3–4:</strong> Hector combat skills. Basic infantry foundation for Bear Hunt participation.</li>
  <li><strong>Month 2:</strong> Bradley + Blanchette. Two strong free heroes that carry you to mid-game.</li>
  <li><strong>Month 3+:</strong> Save shards for Jessie or Jeronimo. One maxed S-tier > three half-built A-tiers.</li>
  <li><strong>Ongoing:</strong> Hall of Heroes every week. This is your primary shard source as F2P.</li>
</ol>

<h2>Rally Mechanics: Which Skills Actually Matter</h2>

<p>Understanding rally mechanics separates good players from great ones:</p>

<ul class="blog-checklist">
  <li>Rally <strong>leader</strong> activates all 9 hero skills from their 3 assigned heroes</li>
  <li>Rally <strong>joiners</strong> only contribute their first hero's top-right skill</li>
  <li>This means: joiner hero quality is almost entirely determined by one single skill</li>
  <li>Jessie's top-right skill is the best rally joiner skill in the current meta</li>
  <li class="bad">Having a mediocre top-right skill makes an otherwise strong hero useless in rallies</li>
</ul>

<h2>Should You Use White Bot for Hero Expeditions?</h2>

<p>Hero expeditions generate passive resources continuously — but only if your heroes aren't sitting idle. Manual players check in every few hours; <a href="/">White Bot</a> manages expedition scheduling automatically, keeping all hero slots active and rotating before stamina caps out. Over 30 days, this compounds into thousands of extra resources with zero manual input.</p>
`,
    },
    es: {
      title: 'Tier List de Héroes en Whiteout Survival 2025 — Los Mejores Rankeados',
      description: 'Tier list completo de héroes para Whiteout Survival. Mejores héroes para combate, caza del oso, recolección y rally. Incluye ruta F2P.',
      keywords: 'tier list héroes whiteout survival, mejores héroes whiteout survival, Jessie whiteout survival, héroes F2P whiteout survival',
      body: `
<div class="blog-hero-banner blog-hero-banner-heroes"><div class="blog-hero-banner-content"><span class="blog-hero-banner-tag">⚔️ Guía de Héroes</span><p class="blog-hero-banner-title">Elige bien. Invierte una vez.<br/>Gana para siempre.</p></div></div>

<p>Elegir los héroes correctos en Whiteout Survival es la decisión que más separa a los jugadores que crecen rápido de los que se estancan. A diferencia de edificios e investigaciones, el desarrollo de héroes es difícil de revertir: los manuales de habilidades y fragmentos gastados en los héroes equivocados no vuelven. Este tier list cubre todas las categorías para ayudarte a invertir bien.</p>

<div class="blog-callout">
  <strong>Cómo funcionan los héroes</strong>
  Cada héroe tiene dos conjuntos de habilidades separados: Exploración (contenido PvE del mapa) y Expedición (producción en la ciudad). Subir uno NO mejora el otro. Los participantes en rallies solo aportan su habilidad superior derecha — esa única habilidad puede definir el valor de un héroe.
</div>

<h2>Nivel S: Desarrolla Estos Primero</h2>

<div class="blog-table-wrap">
  <table>
    <tr><th>Héroe</th><th>Nivel</th><th>Rol</th><th>Por qué son esenciales</th></tr>
    <tr>
      <td><strong>Jessie</strong></td>
      <td><span class="blog-tier-s">S+</span></td>
      <td>Líder de Rally</td>
      <td>Mejor daño en Caza del Oso del juego. El buff de daño porcentual escala mejor que cualquier alternativa. Imprescindible para líderes de rally.</td>
    </tr>
    <tr>
      <td><strong>Jeronimo</strong></td>
      <td><span class="blog-tier-s">S+</span></td>
      <td>Ofensiva / PvP</td>
      <td>Pasiva única: buffs de Ataque y Defensa de tropas en toda la cuenta, incluso sin estar desplegado. Cada batalla se beneficia de haberlo subido de nivel.</td>
    </tr>
    <tr>
      <td><strong>Patrick</strong></td>
      <td><span class="blog-tier-s">S</span></td>
      <td>Defensa / Guarnición</td>
      <td>Mejor héroe defensivo. Curación pasiva de tropas, aumenta la salud de las tropas. Esencial para defensa de guarnición y eventos de kill.</td>
    </tr>
    <tr>
      <td><strong>Bahiti</strong></td>
      <td><span class="blog-tier-s">S</span></td>
      <td>Combate / Rally</td>
      <td>Daño a un solo objetivo excepcional. Especialista en caza de bestias. Su habilidad superior derecha es una de las mejores para participar en rallies.</td>
    </tr>
  </table>
</div>

<h2>Nivel A: Sólidos en Todo</h2>

<div class="blog-table-wrap">
  <table>
    <tr><th>Héroe</th><th>Nivel</th><th>Mejor uso</th></tr>
    <tr><td><strong>Gina</strong></td><td><span class="blog-tier-a">A</span></td><td>Caza de bestias PvE — reduce el coste de stamina hasta un 20%, ahorrando miles de gemas</td></tr>
    <tr><td><strong>Molly</strong></td><td><span class="blog-tier-a">A</span></td><td>Excelente participante de rally con buenos bonos de ataque de marcha</td></tr>
    <tr><td><strong>Natalia</strong></td><td><span class="blog-tier-a">A</span></td><td>Especialista en lanceros, perfecta para composiciones mixtas</td></tr>
    <tr><td><strong>Zinman</strong></td><td><span class="blog-tier-a">A</span></td><td>Ancla de defensa de infantería, buena combinación con Patrick en guarnición</td></tr>
  </table>
</div>

<div class="blog-section-banner blog-section-banner-dark">🏆 📊 🎯</div>

<h2>Héroes Recolectores: Una Categoría Propia</h2>

<p>Estos héroes se valoran únicamente por eficiencia de recolección, no por combate. No los juzgues por sus habilidades de combate — sube solo su línea de habilidades de recolección.</p>

<div class="blog-table-wrap">
  <table>
    <tr><th>Héroe</th><th>Recurso</th><th>Prioridad</th></tr>
    <tr><td><strong>Eugene</strong></td><td>Madera</td><td><span class="blog-tier-s">Esencial</span> — la madera es el recurso #1 más consumido</td></tr>
    <tr><td><strong>Cloris</strong></td><td>Carne</td><td><span class="blog-tier-a">Alta</span> — las tropas comen constantemente</td></tr>
    <tr><td><strong>Smith</strong></td><td>Hierro</td><td><span class="blog-tier-b">Media</span> — el hierro se vuelve crítico tras el Horno 20</td></tr>
    <tr><td><strong>Charlie</strong></td><td>Mixto</td><td><span class="blog-tier-c">Flexible</span> — recolector comodín de apoyo</td></tr>
  </table>
</div>

<div class="blog-callout blog-callout-warn">
  <strong>La trampa de los héroes recolectores</strong>
  Muchos jugadores nuevos suben las habilidades de combate de héroes recolectores "por si acaso". Es un desperdicio. Mantenlos en el nivel mínimo para desbloquear su habilidad de recolección. Punto.
</div>

<h2>Hoja de Ruta de Inversión en Héroes F2P</h2>

<ol class="blog-step-list">
  <li><strong>Semanas 1–2:</strong> Mia al máximo. Es gratis y te mantiene vivo en cada evento temprano.</li>
  <li><strong>Semanas 3–4:</strong> Habilidades de combate de Héctor. Base de infantería para la Caza del Oso.</li>
  <li><strong>Mes 2:</strong> Bradley + Blanchette. Dos héroes gratuitos fuertes que te llevan a mitad de partida.</li>
  <li><strong>Mes 3+:</strong> Ahorra fragmentos para Jessie o Jeronimo. Un S-tier al máximo supera a tres A-tier a medias.</li>
  <li><strong>Siempre:</strong> Salón de Héroes cada semana. Es tu fuente principal de fragmentos siendo F2P.</li>
</ol>

<h2>Mecánica de Rallies: Qué Habilidades Importan Realmente</h2>

<ul class="blog-checklist">
  <li>El <strong>líder</strong> del rally activa las 9 habilidades de héroe de sus 3 héroes asignados</li>
  <li>Los <strong>participantes</strong> solo aportan la habilidad superior derecha de su primer héroe</li>
  <li>Esto significa: la calidad del héroe participante depende casi por completo de una sola habilidad</li>
  <li>La habilidad superior derecha de Jessie es la mejor habilidad de participante en el meta actual</li>
  <li class="bad">Una habilidad superior derecha mediocre hace inútil en rallies a un héroe por lo demás fuerte</li>
</ul>
`,
    },
  },

  // ─────────────────────────────────────────────
  // ARTICLE 4: BEAR HUNT GUIDE
  // ─────────────────────────────────────────────
  {
    slug: 'whiteout-survival-bear-hunt-guide',
    publishedAt: '2025-06-05',
    readingMinutes: 8,
    en: {
      title: 'Bear Hunt in Whiteout Survival: How to Maximize Your Damage (2025)',
      description: 'Complete guide to the Bear Hunt event in Whiteout Survival. Best heroes, troop formations, rally strategy and tips to get top rewards every time.',
      keywords: 'whiteout survival bear hunt, bear hunt guide, whiteout survival raging bear, best heroes bear hunt whiteout survival',
      body: `
<div class="blog-hero-banner blog-hero-banner-bear"><div class="blog-hero-banner-content"><span class="blog-hero-banner-tag">🐻 Bear Hunt</span><p class="blog-hero-banner-title">30 minutes. Every 48 hours.<br/>Don't waste a single rally.</p></div></div>

<p>The Bear Hunt is one of the most important recurring events in Whiteout Survival. Every 48 hours, a Raging Bear spawns for your alliance — and what happens in those 30 minutes determines who gets Hardened Alloys for Chief Gear, and who watches others upgrade while they fall behind. This guide covers everything you need to maximize your damage and rewards.</p>

<div class="blog-stat-row">
  <div class="blog-stat-box"><span class="bsb-num">30</span><span class="bsb-label">Minutes per event</span></div>
  <div class="blog-stat-box"><span class="bsb-num">48h</span><span class="bsb-label">Event frequency</span></div>
  <div class="blog-stat-box"><span class="bsb-num">+25%</span><span class="bsb-label">Arrowhead damage per level</span></div>
  <div class="blog-stat-box"><span class="bsb-num">5</span><span class="bsb-label">Arrowhead max level</span></div>
</div>

<h2>Why Bear Hunt Matters</h2>
<p>Bear Hunt is the primary source of <strong>Hardened Alloys</strong> in the game — the material required to upgrade Chief Gear. Chief Gear provides some of the most impactful permanent stat bonuses in the game, and missing Bear Hunts means your gear progression falls irreversibly behind alliance members who show up every event.</p>

<div class="blog-callout">
  <strong>The compounding effect</strong>
  A player who maximises Bear Hunt damage for 60 days has dramatically better Chief Gear than one who participates minimally. Gear upgrades take weeks — start optimising now, not later.
</div>

<h2>Optimal Troop Formation</h2>

<div class="blog-table-wrap">
  <table>
    <tr><th>Formation</th><th>Use case</th><th>Why</th></tr>
    <tr><td><strong>60% Marksman / 20% Infantry / 20% Lancer</strong></td><td>Bear Hunt (default)</td><td>Marksmen deal the highest sustained damage against a single high-HP target</td></tr>
    <tr><td>50% Infantry / 30% Marksman / 20% Lancer</td><td>PvP / Mixed</td><td>Better survivability for player vs player encounters</td></tr>
    <tr><td>60% Infantry / 40% Lancer</td><td>Defense</td><td>Maximum durability for garrison and city defense</td></tr>
  </table>
</div>

<p>Save your Bear Hunt formation as a preset — you want to load it with one tap at the start of the event, not fumble with numbers while the 30-minute timer burns.</p>

<h2>Best Heroes for Bear Hunt</h2>

<div class="blog-table-wrap">
  <table>
    <tr><th>Hero</th><th>Role</th><th>Priority</th></tr>
    <tr><td><strong>Jessie</strong></td><td>Rally Leader</td><td><span class="blog-tier-s">Non-negotiable</span> — her percentage-based damage buff is unmatched for single-target high-HP fights</td></tr>
    <tr><td><strong>Jeronimo</strong></td><td>Support / Rally Leader</td><td><span class="blog-tier-s">S-tier</span> — account-wide passive boosts every rally you join</td></tr>
    <tr><td><strong>Bahiti</strong></td><td>Rally Joiner</td><td><span class="blog-tier-a">Excellent</span> — top-right skill is one of the best for damage in bear rallies</td></tr>
    <tr><td><strong>Gina</strong></td><td>Beast Hunting</td><td><span class="blog-tier-a">Good</span> — reduces stamina cost, letting you hunt more prep beasts for Arrowheads</td></tr>
  </table>
</div>

<h2>Explosive Arrowheads: The Hidden Multiplier</h2>

<div class="blog-section-banner blog-section-banner-dark">🏹 💥 🎯</div>

<p>Most players focus only on heroes and formation — but Explosive Arrowheads can add up to <strong>+125% damage</strong> (25% per level × 5 levels). This is enormous and often overlooked.</p>

<ul class="blog-checklist">
  <li>Hunt wilderness beasts on the map between Bear Hunt events to earn Arrowhead materials</li>
  <li>Upgrade the Hunting Trap to increase Arrowhead level (max level 5)</li>
  <li>Each Arrowhead level adds a flat 25% damage bonus to your Bear Hunt attacks</li>
  <li>Gina reduces the stamina cost of beast hunts by up to 20% — use her for prep farming</li>
  <li class="bad">Don't enter Bear Hunt with Level 0 Arrowheads — you're leaving 125% free damage on the table</li>
</ul>

<h2>Rally Strategy: More Rallies = More Damage</h2>

<p>The math is simple: each rally you participate in deals damage to the bear. More rallies in 30 minutes = more total damage = better rewards. Everything in your Bear Hunt strategy should optimize for rally count.</p>

<ol class="blog-step-list">
  <li><strong>Position close to the bear.</strong> March time is the bottleneck. City placement within rally range is critical. Shorter marches = more rally slots completed in 30 minutes.</li>
  <li><strong>Keep one march queue always open.</strong> Enable Auto-Join Rally in settings so you never miss a rally while managing other queues.</li>
  <li><strong>Don't waste time between rallies.</strong> The moment one march returns, send the next. Every gap is damage lost.</li>
  <li><strong>Coordinate with your alliance.</strong> More members rallying = higher damage per rally = better shared rewards. Alliances with active Bear Hunt coordination consistently outperform disorganized ones.</li>
</ol>

<h2>Rally Leader vs Rally Joiner</h2>

<div class="blog-callout blog-callout-warn">
  <strong>If you're the rally leader</strong>
  Assign Jessie, Jeronimo, and your best third hero. All 9 of their skills activate. This is the highest-impact role in the event.
</div>

<div class="blog-callout">
  <strong>If you're a rally joiner</strong>
  Only your first hero's top-right skill contributes significantly. Make sure your primary hero has a strong damage top-right skill (Jessie or Bahiti are ideal). The rest matters much less.
</div>

<h2>Automating Bear Hunt Participation</h2>

<p>The 48-hour cycle means Bear Hunts happen at all hours — including when you're asleep or at work. <a href="/">White Bot</a> keeps one march queue permanently open for Auto-Join Rally, ensuring you participate in every Bear Hunt event regardless of your schedule. Over a month, this difference in Hardened Alloy accumulation translates directly into faster Chief Gear progression.</p>
`,
    },
    es: {
      title: 'Caza del Oso en Whiteout Survival: Cómo Maximizar tu Daño (2025)',
      description: 'Guía completa del evento Caza del Oso en Whiteout Survival. Mejores héroes, formaciones de tropas, estrategia de rally y consejos para conseguir las mejores recompensas.',
      keywords: 'caza del oso whiteout survival, guía caza del oso, oso furioso whiteout survival, mejores héroes caza del oso',
      body: `
<div class="blog-hero-banner blog-hero-banner-bear"><div class="blog-hero-banner-content"><span class="blog-hero-banner-tag">🐻 Caza del Oso</span><p class="blog-hero-banner-title">30 minutos. Cada 48 horas.<br/>No desperdicies ni un rally.</p></div></div>

<p>La Caza del Oso es uno de los eventos recurrentes más importantes de Whiteout Survival. Cada 48 horas, aparece un Oso Furioso para tu alianza — y lo que ocurra en esos 30 minutos determina quién consigue Aleaciones Endurecidas para el Equipo del Jefe, y quién ve a otros mejorar mientras se queda atrás. Esta guía cubre todo lo que necesitas para maximizar tu daño y recompensas.</p>

<div class="blog-stat-row">
  <div class="blog-stat-box"><span class="bsb-num">30</span><span class="bsb-label">Minutos por evento</span></div>
  <div class="blog-stat-box"><span class="bsb-num">48h</span><span class="bsb-label">Frecuencia del evento</span></div>
  <div class="blog-stat-box"><span class="bsb-num">+25%</span><span class="bsb-label">Daño por nivel de flecha</span></div>
  <div class="blog-stat-box"><span class="bsb-num">5</span><span class="bsb-label">Nivel máximo de flecha</span></div>
</div>

<h2>Por Qué Importa la Caza del Oso</h2>
<p>La Caza del Oso es la principal fuente de <strong>Aleaciones Endurecidas</strong> del juego — el material necesario para mejorar el Equipo del Jefe. El Equipo del Jefe proporciona algunas de las bonificaciones de estadísticas permanentes más impactantes, y perderse Cazas del Oso significa que tu progresión de equipo queda irreversiblemente por detrás de los miembros que sí participan.</p>

<div class="blog-callout">
  <strong>El efecto acumulativo</strong>
  Un jugador que maximiza el daño en Caza del Oso durante 60 días tiene un Equipo del Jefe significativamente mejor que uno que participa mínimamente. Las mejoras de equipo tardan semanas — empieza a optimizar ahora, no más tarde.
</div>

<h2>Formación de Tropas Óptima</h2>

<div class="blog-table-wrap">
  <table>
    <tr><th>Formación</th><th>Uso</th><th>Por qué</th></tr>
    <tr><td><strong>60% Tiradores / 20% Infantería / 20% Lanceros</strong></td><td>Caza del Oso (por defecto)</td><td>Los tiradores hacen el mayor daño sostenido contra un objetivo único con alta vida</td></tr>
    <tr><td>50% Infantería / 30% Tiradores / 20% Lanceros</td><td>PvP / Mixto</td><td>Mejor supervivencia para enfrentamientos jugador vs jugador</td></tr>
    <tr><td>60% Infantería / 40% Lanceros</td><td>Defensa</td><td>Máxima durabilidad para guarnición y defensa de ciudad</td></tr>
  </table>
</div>

<h2>Mejores Héroes para Caza del Oso</h2>

<div class="blog-table-wrap">
  <table>
    <tr><th>Héroe</th><th>Rol</th><th>Prioridad</th></tr>
    <tr><td><strong>Jessie</strong></td><td>Líder de Rally</td><td><span class="blog-tier-s">Imprescindible</span> — su buff de daño porcentual no tiene rival en combates de un solo objetivo con alta vida</td></tr>
    <tr><td><strong>Jeronimo</strong></td><td>Apoyo / Líder</td><td><span class="blog-tier-s">S-tier</span> — las pasivas de cuenta amplían cada rally en el que participas</td></tr>
    <tr><td><strong>Bahiti</strong></td><td>Participante</td><td><span class="blog-tier-a">Excelente</span> — habilidad superior derecha entre las mejores para daño en rallies del oso</td></tr>
    <tr><td><strong>Gina</strong></td><td>Caza de Bestias</td><td><span class="blog-tier-a">Buena</span> — reduce coste de stamina, permitiendo cazar más bestias previas para conseguir Flechas</td></tr>
  </table>
</div>

<h2>Flechas Explosivas: El Multiplicador Oculto</h2>

<div class="blog-section-banner blog-section-banner-dark">🏹 💥 🎯</div>

<p>La mayoría de jugadores se centra solo en héroes y formación — pero las Flechas Explosivas pueden añadir hasta <strong>+125% de daño</strong> (25% por nivel × 5 niveles). Es enorme y a menudo se ignora.</p>

<ul class="blog-checklist">
  <li>Caza bestias en el mapa entre eventos de Caza del Oso para conseguir materiales de Flechas</li>
  <li>Mejora la Trampa de Caza para subir el nivel de Flecha (nivel máximo 5)</li>
  <li>Cada nivel de Flecha añade un 25% de bono de daño a tus ataques en Caza del Oso</li>
  <li>Gina reduce el coste de stamina de cazar bestias hasta un 20% — úsala para farmear la preparación</li>
  <li class="bad">No entres a Caza del Oso con Flechas de Nivel 0 — estás dejando un 125% de daño gratuito sobre la mesa</li>
</ul>

<h2>Estrategia de Rally: Más Rallies = Más Daño</h2>

<ol class="blog-step-list">
  <li><strong>Posiciónate cerca del oso.</strong> El tiempo de marcha es el cuello de botella. La posición de tu ciudad dentro del radio de rally es crítica.</li>
  <li><strong>Mantén siempre una cola de marcha libre.</strong> Activa Unirse Auto a Rally en ajustes para no perderte ningún rally.</li>
  <li><strong>No pierdas tiempo entre rallies.</strong> En cuanto regrese una marcha, envía la siguiente. Cada pausa es daño perdido.</li>
  <li><strong>Coordina con tu alianza.</strong> Más miembros en rally = mayor daño por rally = mejores recompensas compartidas.</li>
</ol>

<h2>Automatizando la Participación en Caza del Oso</h2>

<p>El ciclo de 48 horas significa que las Cazas del Oso ocurren a cualquier hora — incluso mientras duermes o trabajas. <a href="/">White Bot</a> mantiene permanentemente una cola de marcha abierta para Unirse Auto a Rally, asegurando que participes en cada evento de Caza del Oso independientemente de tu horario. A lo largo de un mes, esta diferencia en acumulación de Aleaciones Endurecidas se traduce directamente en una progresión de Equipo del Jefe más rápida.</p>
`,
    },
  },

  // ─────────────────────────────────────────────
  // ARTICLE 5: STATE VS STATE GUIDE
  // ─────────────────────────────────────────────
  {
    slug: 'whiteout-survival-state-vs-state-guide',
    publishedAt: '2025-06-12',
    readingMinutes: 10,
    en: {
      title: 'State vs State (SvS) Guide — Win the War in Whiteout Survival',
      description: 'Complete SvS guide for Whiteout Survival. Preparation phase, battle mechanics, Sunfire Castle strategy, troop management and how to contribute as F2P.',
      keywords: 'whiteout survival state vs state, SvS guide whiteout survival, sunfire castle whiteout, how to win SvS whiteout survival',
      body: `
<div class="blog-hero-banner blog-hero-banner-svs"><div class="blog-hero-banner-content"><span class="blog-hero-banner-tag">🏰 State vs State</span><p class="blog-hero-banner-title">Two states. 12 hours.<br/>One winner takes all.</p></div></div>

<p>State vs State — or SvS — is Whiteout Survival at its most epic. Two entire states clash in a 12-hour battle phase that determines buffs, bragging rights, and progression advantages for weeks. But winning SvS is less about raw power and more about coordination, timing, and smart resource deployment. This guide covers everything from prep phase to the final castle push.</p>

<div class="blog-stat-row">
  <div class="blog-stat-box"><span class="bsb-num">5</span><span class="bsb-label">Prep days</span></div>
  <div class="blog-stat-box"><span class="bsb-num">12h</span><span class="bsb-label">Battle phase</span></div>
  <div class="blog-stat-box"><span class="bsb-num">16</span><span class="bsb-label">Min furnace level</span></div>
  <div class="blog-stat-box"><span class="bsb-num">2.5h</span><span class="bsb-label">Castle hold to win</span></div>
</div>

<h2>SvS Prerequisites</h2>

<ul class="blog-checklist">
  <li>Furnace level 16 minimum to participate in the battle phase</li>
  <li>State must be at least 12 weeks old</li>
  <li>You can only attack chiefs within ±3 furnace levels of your own</li>
  <li>Preparation phase rewards are earned by all Furnace 1+ players</li>
  <li class="bad">Below Furnace 16? Focus on preparation phase points — avoid the battle phase</li>
</ul>

<h2>The Two Phases Explained</h2>

<h3>Phase 1: Preparation (5 Days)</h3>
<p>The preparation phase is where many alliances win or lose SvS without realising it. Points are scored through daily quests, construction, research, training, gathering, and specific SvS objectives. A state that dominates the preparation phase often snowballs into the battle.</p>

<div class="blog-callout blog-callout-success">
  <strong>Prep phase priorities</strong>
  Complete all daily SvS tasks · Queue long builds and research to complete during prep · Train troops for battle phase reinforcement · Stockpile speed-ups and shields · Coordinate with alliance on point targets
</div>

<h3>Phase 2: Battle (12 Hours)</h3>
<p>The battle phase is a 12-hour warfare window culminating in the fight for Sunfire Castle. The win condition: hold the castle continuously for 2.5 hours.</p>

<div class="blog-section-banner blog-section-banner-dark">🏰 🛡️ ⚔️</div>

<h2>Sunfire Castle: The Win Condition</h2>

<p>Everything in the battle phase points toward this: your state occupying Sunfire Castle for 2.5 consecutive hours. Once the timer completes, your state wins — regardless of total kills or territory held elsewhere.</p>

<ol class="blog-step-list">
  <li><strong>Contest the outer towers first.</strong> If your state holds both towers and the castle, enemy towers won't attack your troops inside the castle. This dramatically reduces defensive pressure.</li>
  <li><strong>Rotate reinforcements.</strong> Garrison troops tire and need rotation. Alliances must coordinate which members reinforce at what time to maintain continuous occupation.</li>
  <li><strong>Shield and heal cycle.</strong> Troops lost defending the castle must be healed immediately and cycled back in. Your Infirmary capacity directly impacts how long you can sustain occupation.</li>
  <li><strong>Communication wins wars.</strong> Organized states with active R4/R5 leadership calling targets and timing consistently beat larger but disorganized opponents.</li>
</ol>

<h2>Troop Management During SvS</h2>

<div class="blog-callout blog-callout-warn">
  <strong>The Infirmary overflow trick</strong>
  During active SvS events, all troops that would normally overflow your Infirmary go to the Enlistment Office instead of being lost. This means you can fight more aggressively — wounded troops are saved, not deleted. Use this window to be more offensive than you normally would.
</div>

<div class="blog-table-wrap">
  <table>
    <tr><th>Scenario</th><th>Recommended formation</th><th>Rationale</th></tr>
    <tr><td>Attacking enemy cities</td><td>50/20/30 (Infantry/Lancer/Marksman)</td><td>Balanced offense with survivability</td></tr>
    <tr><td>Defending castle</td><td>60/40/0 (Infantry/Lancer)</td><td>Maximum tanking capacity</td></tr>
    <tr><td>Rally participation</td><td>60/20/20 (Infantry/Marksman/Lancer)</td><td>Rally leader's call determines optimal mix</td></tr>
  </table>
</div>

<h2>How to Contribute as F2P</h2>

<p>SvS can feel intimidating for free-to-play players surrounded by big spenders. But coordinated F2P contribution is often what separates winning states from losing ones.</p>

<ul class="blog-checklist">
  <li>Complete every preparation phase task — points add up across the whole state</li>
  <li>Join all rally attacks during battle phase — even as a joiner, your damage counts</li>
  <li>Keep Auto-Join Rally active so you never miss an opportunity while managing other actions</li>
  <li>Reinforce the castle when called — garrison numbers matter more than individual power</li>
  <li>Scout before solo attacking — only attack if you're confident of victory</li>
  <li class="bad">Don't solo attack targets 3+ furnace levels above you — guaranteed loss</li>
  <li class="bad">Don't leave troops outside without a shield when you log off during battle phase</li>
</ul>

<h2>Rewards: Why Winning Matters</h2>

<div class="blog-table-wrap">
  <table>
    <tr><th>Reward</th><th>Winning state</th><th>Losing state</th></tr>
    <tr><td>Troop training speed</td><td>+30% for 2 weeks</td><td>Standard rate</td></tr>
    <tr><td>Resource production</td><td>+20% for 2 weeks</td><td>Standard rate</td></tr>
    <tr><td>Healing speed</td><td>+25% for 2 weeks</td><td>Standard rate</td></tr>
    <tr><td>Supreme President</td><td>Elected by winning state</td><td>—</td></tr>
    <tr><td>Personal loot</td><td>Enhanced chest rewards</td><td>Participation rewards</td></tr>
  </table>
</div>

<p>The compounding effect of winning two or three consecutive SvS events is significant: faster troop training, faster healing, and better resource production create a snowball that grows the entire state's power for the next matchup.</p>

<h2>24/7 Presence: The Underrated Advantage</h2>

<p>The 12-hour battle phase spans time zones. Alliances that can field active players throughout the full window have a structural advantage over those that go quiet during off-hours. This is where automation becomes strategic: <a href="/">White Bot</a> users maintain active rally participation and resource queues even during sleep hours, arriving at the SvS battle phase with full resource stockpiles and continuous troop training — not scrambling to catch up.</p>
`,
    },
    es: {
      title: 'Guía de Estado vs Estado (SvS) — Gana la Guerra en Whiteout Survival',
      description: 'Guía completa de SvS para Whiteout Survival. Fase de preparación, mecánicas de batalla, estrategia del Castillo Solar, gestión de tropas y cómo contribuir siendo F2P.',
      keywords: 'whiteout survival estado vs estado, guía SvS whiteout survival, castillo solar whiteout, cómo ganar SvS whiteout survival',
      body: `
<div class="blog-hero-banner blog-hero-banner-svs"><div class="blog-hero-banner-content"><span class="blog-hero-banner-tag">🏰 Estado vs Estado</span><p class="blog-hero-banner-title">Dos estados. 12 horas.<br/>Un solo ganador.</p></div></div>

<p>Estado vs Estado — o SvS — es Whiteout Survival en su máxima expresión. Dos estados enteros se enfrentan en una fase de batalla de 12 horas que determina buffs, derechos de presumir y ventajas de progresión durante semanas. Pero ganar el SvS tiene menos que ver con el poder bruto y más con la coordinación, el momento adecuado y el despliegue inteligente de recursos.</p>

<div class="blog-stat-row">
  <div class="blog-stat-box"><span class="bsb-num">5</span><span class="bsb-label">Días de preparación</span></div>
  <div class="blog-stat-box"><span class="bsb-num">12h</span><span class="bsb-label">Fase de batalla</span></div>
  <div class="blog-stat-box"><span class="bsb-num">16</span><span class="bsb-label">Nivel mínimo de horno</span></div>
  <div class="blog-stat-box"><span class="bsb-num">2.5h</span><span class="bsb-label">Tiempo para ganar castillo</span></div>
</div>

<h2>Requisitos del SvS</h2>

<ul class="blog-checklist">
  <li>Nivel de Horno 16 mínimo para participar en la fase de batalla</li>
  <li>El estado debe tener al menos 12 semanas de antigüedad</li>
  <li>Solo puedes atacar jefes dentro de ±3 niveles de Horno del tuyo</li>
  <li>Las recompensas de fase de preparación las ganan todos los jugadores desde Horno 1</li>
  <li class="bad">¿Por debajo del Horno 16? Céntrate en puntos de fase de preparación — evita la fase de batalla</li>
</ul>

<h2>Las Dos Fases Explicadas</h2>

<h3>Fase 1: Preparación (5 Días)</h3>
<p>La fase de preparación es donde muchas alianzas ganan o pierden el SvS sin darse cuenta. Los puntos se consiguen con tareas diarias, construcción, investigación, entrenamiento, recolección y objetivos específicos de SvS. Un estado que domina la preparación suele arrasar en la batalla.</p>

<div class="blog-callout blog-callout-success">
  <strong>Prioridades de la fase de preparación</strong>
  Completar todas las tareas diarias de SvS · Poner en cola mejoras largas y research para completar durante la prep · Entrenar tropas para refuerzo en la fase de batalla · Acumular aceleraciones y escudos · Coordinar con la alianza los objetivos de puntos
</div>

<h3>Fase 2: Batalla (12 Horas)</h3>
<p>La fase de batalla es una ventana de guerra de 12 horas que culmina en la lucha por el Castillo Solar. La condición de victoria: mantener el castillo continuamente durante 2,5 horas.</p>

<div class="blog-section-banner blog-section-banner-dark">🏰 🛡️ ⚔️</div>

<h2>Castillo Solar: La Condición de Victoria</h2>

<ol class="blog-step-list">
  <li><strong>Conquista primero las torres exteriores.</strong> Si tu estado controla ambas torres y el castillo, las torres enemigas no atacarán tus tropas dentro del castillo.</li>
  <li><strong>Rota los refuerzos.</strong> Las tropas de guarnición se agotan y necesitan rotación. Las alianzas deben coordinar qué miembros refuerzan en qué momento.</li>
  <li><strong>Ciclo de escudo y curación.</strong> Las tropas perdidas defendiendo el castillo deben curarse de inmediato y volver. La capacidad de tu Enfermería impacta directamente en cuánto tiempo puedes mantener la ocupación.</li>
  <li><strong>La comunicación gana guerras.</strong> Los estados organizados con liderazgo R4/R5 activo llamando objetivos y tiempos sistemáticamente superan a oponentes más grandes pero desorganizados.</li>
</ol>

<h2>Gestión de Tropas Durante el SvS</h2>

<div class="blog-callout blog-callout-warn">
  <strong>El truco del desbordamiento de Enfermería</strong>
  Durante eventos SvS activos, todas las tropas que normalmente desbordarían tu Enfermería van al Registro de Alistamiento en vez de perderse. Esto significa que puedes combatir más agresivamente — las tropas heridas se salvan, no se eliminan. Usa esta ventana para ser más ofensivo de lo habitual.
</div>

<h2>Cómo Contribuir Siendo F2P</h2>

<ul class="blog-checklist">
  <li>Completa todas las tareas de la fase de preparación — los puntos suman en todo el estado</li>
  <li>Únete a todos los rallies de ataque durante la fase de batalla — incluso como participante, tu daño cuenta</li>
  <li>Mantén Unirse Auto a Rally activo para no perder ninguna oportunidad</li>
  <li>Refuerza el castillo cuando se te llame — el número de guarnición importa más que el poder individual</li>
  <li>Explora antes de atacar en solitario — solo ataca si estás seguro de ganar</li>
  <li class="bad">No ataques en solitario objetivos con 3+ niveles de Horno por encima del tuyo — derrota garantizada</li>
  <li class="bad">No dejes tropas fuera sin escudo cuando te desconectes durante la fase de batalla</li>
</ul>

<h2>Recompensas: Por Qué Importa Ganar</h2>

<div class="blog-table-wrap">
  <table>
    <tr><th>Recompensa</th><th>Estado ganador</th><th>Estado perdedor</th></tr>
    <tr><td>Velocidad de entrenamiento</td><td>+30% durante 2 semanas</td><td>Velocidad estándar</td></tr>
    <tr><td>Producción de recursos</td><td>+20% durante 2 semanas</td><td>Velocidad estándar</td></tr>
    <tr><td>Velocidad de curación</td><td>+25% durante 2 semanas</td><td>Velocidad estándar</td></tr>
    <tr><td>Presidente Supremo</td><td>Elegido por el estado ganador</td><td>—</td></tr>
  </table>
</div>

<h2>Presencia 24/7: La Ventaja Subestimada</h2>

<p>La fase de batalla de 12 horas abarca zonas horarias. Las alianzas que pueden tener jugadores activos durante toda la ventana tienen una ventaja estructural. Aquí es donde la automatización se vuelve estratégica: los usuarios de <a href="/">White Bot</a> mantienen participación activa en rallies y colas de recursos incluso durante las horas de sueño, llegando a la fase de batalla del SvS con reservas de recursos completas y entrenamiento de tropas continuo.</p>
`,
    },
  },

];

export function getPost(slug: string): BlogPost | undefined {
  return posts.find((p) => p.slug === slug);
}
